// WHAT: Fetches whatever GLB piece models are present in `src/chesspieces/` and
//       hands back the ones that arrived, normalised and ready to clone.
// HOW:  One GLTFLoader pass. Each model is measured, scaled so its footprint
//       fills the plinth the lattice reserves, and shifted so its feet sit at
//       y = 0 and its centre on the axis — the board places pieces by centroid
//       and platform height, so anything else stands off its square or sinks
//       into it. Models that fail to load are left out rather than throwing:
//       a missing file means the procedural piece is used, not a blank board.
// WHY:  BUILD_PLAN §2 — "exported GLB pieces often have their origin at the
//       model centre, not the feet. Measure the bounding box in the loader and
//       offset once, never per-piece magic numbers." This is that loader.

import { Box3, Group, Mesh, Vector3 } from 'three';
import type { Object3D, WebGLRenderer } from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';

import type { Color, PieceType } from '@domain/chess/types';

import { PIECE_SETS, folderOf } from './pieceSets';
import type { PieceSetDef } from './pieceSets';

/** `${color}-${type}`, the key a factory looks a model up by. */
export type ModelKey = `${Color}-${PieceType}`;

/**
 * How tall an unadjusted piece stands, in cell widths.
 *
 * The set is modelled height-normalised — every piece is exactly 2.00 units
 * tall — so the models carry no proportions of their own and this is what
 * decides the scale. A pawn takes no adjustment, so this is the pawn.
 */
const PIECE_HEIGHT = 0.74;

/**
 * How much of a cell any one piece may occupy, as a fraction of cell width.
 *
 * latticeWarp guarantees every cell an inradius of 0.30 cells, which is what
 * the procedural plinth is built to. This is deliberately a little over that:
 * pieces sized strictly to the guarantee read as small on a board whose cells
 * are mostly bigger than the tightest one, so a model is allowed to overhang
 * the very narrowest squares slightly.
 *
 * A ceiling on each piece separately, never on the set. Height decides how big
 * everything is; this only stops one broad piece from fouling its square, and
 * it stops it by trimming that piece.
 */
const FOOTPRINT = 0.36;

/**
 * How far a piece may reach along its own file, from its centre, as a
 * fraction of cell width: the whole of its own square, and no further.
 *
 * FOOTPRINT is a radius, and treating every piece as round is right for a
 * figure but wrong for a gun. A field gun is long and narrow, and its length
 * runs along the file, where the nearest thing is the piece a whole square
 * ahead — not beside it, where FOOTPRINT's neighbours are. Checked as a
 * circle, the WW1 guns were shrunk until their barrels would fit sideways
 * between two neighbours that are nowhere near them. A round piece is still
 * limited by FOOTPRINT, which is the smaller of the two, so nothing round
 * changes.
 */
const ALONG_FILE = 0.5;

/**
 * Which way the models face, in radians about Y, before the board turns them.
 *
 * PieceLayer yaws White to −Z and Black to +Z, so a model is expected to face
 * −Z at rest. These face +Z — seen from above, every shield and spear pointed
 * back toward its own side rather than at the enemy — so the set is given a
 * half-turn on the way in. One number: if a later export faces −Z, this goes
 * to 0 and nothing else changes.
 */
const MODEL_FACING = Math.PI;

/**
 * Per-type nudges to the shared scale, set by eye.
 *
 * These carry the whole of the set's proportions, not a correction to them.
 * Every model is exactly 2.00 units tall, so left alone the king would stand
 * level with a pawn; the scale decides how big a piece is and this decides
 * which piece.
 *
 * `scale` grows the whole piece; `width` grows only its plan, for a piece that
 * is tall enough but reads thin.
 *
 * The order they produce is a chess set's: pawn, rook, knight, bishop, queen,
 * king. The pawn takes none by definition — PIECE_HEIGHT is the pawn.
 *
 * Width multipliers do not climb with rank the way heights do, because breadth
 * is not rank and the models do not start equally broad. The rook is the widest
 * model in the set by a distance and is meant to stay that way on the board —
 * a castle reads by its mass, not its height — while a robed queen starts
 * narrow and needs help to fill the same square.
 */
interface SizeAdjust {
  /** Uniform multiplier on the shared scale. */
  readonly scale?: number;
  /** Extra multiplier on x and z only. */
  readonly width?: number;
}

const SIZE_ADJUST: Readonly<Partial<Record<PieceType, SizeAdjust>>> = {
  rook: { scale: 1.2, width: 1.16 },
  bishop: { scale: 1.3, width: 1.25 },
  knight: { scale: 1.22, width: 1.12 },
  queen: { scale: 1.4, width: 1.25 },
  king: { scale: 1.45, width: 1.25 },
};

/**
 * Every compressed GLB, found at build time by its filename.
 *
 * Deliberately the `compressed/` folder and not the masters beside it. The
 * masters carry a 2048x2048 JPEG each and come to 37 MB; what is served is the
 * same geometry and the same 2048x2048 texture re-encoded as ETC1S, at about a
 * third of the bytes and none of the decode. `npm run pieces` is what puts
 * files here — see scripts/compress-pieces.mjs.
 *
 * Vite resolves this glob when it bundles, so adding a piece is adding a file
 * and running that script: the result is loaded, hashed and served with no code
 * change here. A name that does not match the convention is ignored rather than
 * guessed at.
 *
 * One folder per set (medieval/, ww1/, …), and every set is in this glob. That
 * costs nothing at page load: an eager `?url` glob is a table of URL strings,
 * not the models, and only the set asked for is ever fetched.
 */
const FILES: Readonly<Record<string, string>> = import.meta.glob(
  '../../chesspieces/compressed/*/*.glb',
  {
    eager: true,
    query: '?url',
    import: 'default',
  },
);

const TYPES: readonly PieceType[] = ['pawn', 'knight', 'bishop', 'rook', 'queen', 'king'];

/**
 * The piece a filename names, read against its set's armies.
 *
 * Two words either way round: `rook_white.glb` in the original set,
 * `anzac_pawn.glb` in the era sets. One word must be a piece; the other is a
 * colour, or an army the set says plays one. Anything else is skipped.
 *
 * Exported so the convention is testable without a browser: it is the whole
 * contract for adding a piece, and a silent mismatch would look exactly like a
 * model that failed to load.
 */
export function modelKeyFor(path: string, armies: PieceSetDef = COLOUR_NAMES): ModelKey | null {
  const name =
    path
      .split('/')
      .pop()
      ?.replace(/\.glb$/i, '')
      .toLowerCase() ?? '';
  const words = name.split('_');
  if (words.length !== 2) return null;
  const typeWord = words.find((w) => TYPES.includes(w as PieceType));
  if (typeWord === undefined) return null;
  const sideWord = words[0] === typeWord ? words[1] : words[0];
  const color: Color | null =
    sideWord === armies.white || sideWord === 'white'
      ? 'white'
      : sideWord === armies.black || sideWord === 'black'
        ? 'black'
        : null;
  if (color === null) return null;
  return `${color}-${typeWord as PieceType}`;
}

/** A set that names its sides by colour, as the original one does. */
const COLOUR_NAMES: PieceSetDef = { white: 'white', black: 'black' };

export type PieceModels = ReadonlyMap<ModelKey, Object3D>;

/**
 * The set every board uses unless it has a reason to use another. It is the
 * original hand-made set, filed under medieval/ when the era sets arrived.
 */
export const DEFAULT_PIECE_SET = 'medieval';

/** The set a compressed model belongs to: the folder it sits in. */
export function pieceSetOf(path: string): string | null {
  const parts = path.split('/');
  return parts.length >= 2 ? (parts[parts.length - 2] ?? null) : null;
}

/**
 * The pieces a set has models for, read from the bundle's file list without
 * fetching anything — so the game can decide whether to offer a set before it
 * spends a byte on it.
 */
export function piecesInSet(
  set: string,
  files: Readonly<Record<string, string>> = FILES,
): readonly ModelKey[] {
  const armies = PIECE_SETS[set] ?? COLOUR_NAMES;
  const keys = new Set<ModelKey>();
  for (const path of Object.keys(files)) {
    if (pieceSetOf(path) !== folderOf(set)) continue;
    const key = modelKeyFor(path, armies);
    if (key !== null) keys.add(key);
  }
  return [...keys];
}

/**
 * All twelve: six pieces, two sides. A set is offered to players only when it
 * is complete — an ANZAC pawn beside a medieval knight is a work in progress,
 * not a board — and until then it can still be previewed with `?pieces=`.
 */
export function isCompleteSet(set: string, files?: Readonly<Record<string, string>>): boolean {
  return piecesInSet(set, files).length === TYPES.length * 2;
}

export async function loadPieceModels(
  unit: number,
  renderer: WebGLRenderer,
  set: string = DEFAULT_PIECE_SET,
): Promise<PieceModels> {
  const loader = new GLTFLoader();

  /*
    The textures are ETC1S inside KTX2, so the GPU is handed them compressed and
    never decodes an image. detectSupport needs the live renderer to know which
    compressed format this GPU actually takes — the transcoder targets several —
    which is why this is called with the running app's renderer rather than
    before there is one.
  */
  const ktx2 = new KTX2Loader()
    .setTranscoderPath(`${import.meta.env.BASE_URL}basis/`)
    .detectSupport(renderer);
  loader.setKTX2Loader(ktx2);

  const armies = PIECE_SETS[set] ?? COLOUR_NAMES;
  const found = Object.entries(FILES)
    .filter(([path]) => pieceSetOf(path) === folderOf(set))
    .map(([path, url]) => ({ key: modelKeyFor(path, armies), url, path }))
    .filter((entry): entry is { key: ModelKey; url: string; path: string } => entry.key !== null);

  const loaded: { key: ModelKey; scene: Object3D; box: Box3 }[] = [];
  await Promise.all(
    found.map(async ({ key, url, path }) => {
      try {
        const gltf = await loader.loadAsync(url);
        faceForward(gltf.scene, path, armies.turn?.[key.split('-')[1] as PieceType] ?? 0);
        loaded.push({ key, scene: gltf.scene, box: new Box3().setFromObject(gltf.scene) });
      } catch (error: unknown) {
        // Not fatal: the board falls back to the piece it already had.
        console.warn(`Could not load ${path}; using the procedural piece.`, error);
      }
    }),
  );

  // The transcoder holds a worker open; nothing else is read through it.
  ktx2.dispose();

  /*
    One scale for the whole set, taken from height.

    The set arrived height-normalised: every model is exactly 2.00 units tall,
    so there are no modelled proportions left to follow and every difference in
    rank has to come from SIZE_ADJUST. Scaling from the widest model — which is
    what this did while the rooks were 2.70 on a 0.98 base — now means the
    rook's waistline decides how big a king is, and the day the rooks were
    remade slimmer it would have grown the whole set by 40%.
  */
  const tallest = Math.max(...loaded.map(({ box }) => box.max.y - box.min.y), Number.EPSILON);
  const scale = (PIECE_HEIGHT * unit) / tallest;

  const models = new Map<ModelKey, Object3D>();
  for (const { key, scene, box } of loaded) {
    const type = key.split('-')[1] as PieceType;
    const adjust = SIZE_ADJUST[type] ?? {};
    const up = scale * (adjust.scale ?? 1);

    /*
      The footprint ceiling is applied per piece, not to the set.

      It used to choose one scale for everybody from whichever model came out
      widest, which is the same coupling D-065 took off the height axis: a rook
      broad enough to fill its own square would have pulled all twelve pieces
      down with it, so the only way to make a castle look like a castle was to
      shrink the whole set. Clamping here costs the piece that is too wide and
      nobody else: a width adjustment that overruns is trimmed back toward the
      modelled width, and a model too broad even as made is shrunk whole —
      never squeezed. See fitToCell.
    */
    const size = new Vector3();
    box.getSize(size);
    // After faceForward every model faces −Z, so x is across the rank and z
    // runs along the file.
    const halfAcross = Math.max(size.x / 2, Number.EPSILON);
    const halfAlong = Math.max(size.z / 2, Number.EPSILON);
    const cap = Math.min((FOOTPRINT * unit) / halfAcross, (ALONG_FILE * unit) / halfAlong);
    const { tall, across, fit } = fitToCell(up, up * (adjust.width ?? 1), cap);
    if (fit === 'shrunk') {
      console.warn(
        `${set}/${type} is broader than a cell even as modelled; shrunk whole, to ${String(Math.round((tall / up) * 100))}%, to keep its shape.`,
      );
    } else if (fit === 'trimmed') {
      console.warn(
        `${set}/${type}'s width adjustment is more than a cell allows; trimmed back toward its modelled width.`,
      );
    }

    const root = stand(scene, box, tall, across);
    root.userData.set = set;
    models.set(key, root);
  }
  return models;
}

/**
 * How big a piece stands, given the height it asks for (`up`), the plan width
 * it asks for (`wanted`), and the widest a cell allows (`cap`), all as scale
 * factors on the model.
 *
 * Trimming the plan is fair only down to the model's own proportions: that
 * undoes a width adjustment, it does not reshape anybody's work. A model
 * broader than a cell even at its natural width is shrunk whole instead, so it
 * keeps the shape it was made with and loses stature rather than being
 * squeezed on one axis. The WW1 rooks are the case that found it: half again
 * the medieval rooks' width, which the old rule narrowed to 83% of what was
 * modelled. Their author chose shape over height, and this is that choice.
 *
 * Exported to be tested: nothing else guards against a piece being distorted.
 */
export function fitToCell(
  up: number,
  wanted: number,
  cap: number,
): { tall: number; across: number; fit: 'as-asked' | 'trimmed' | 'shrunk' } {
  if (cap < up) return { tall: cap, across: cap, fit: 'shrunk' };
  if (wanted > cap) return { tall: up, across: cap, fit: 'trimmed' };
  return { tall: up, across: wanted, fit: 'as-asked' };
}

/**
 * Points the model the way the board expects, ignoring any turn the exporter
 * happened to bake into it.
 *
 * Which way a piece faces is the board's to say — PieceLayer yaws White to
 * −Z and Black to +Z — so a yaw sitting in the file is an accident of
 * authoring rather than information, and it fights the game for control of
 * the same axis. These pawns arrived with exactly 45 degrees on their root
 * node, from a rotation that was never applied before export, and stood on
 * the board facing the corners.
 *
 * Only a turn about Y is removed. A model exported lying on its side has a
 * rotation that means something, and guessing at it would be worse than
 * leaving it and saying so.
 */
function faceForward(scene: Object3D, path: string, turnDeg = 0): void {
  for (const child of scene.children) {
    const { x, z } = child.rotation;
    if (Math.abs(x) > 1e-6 || Math.abs(z) > 1e-6) {
      console.warn(`${path} is rotated off the upright; leaving it as exported.`);
      continue;
    }
    // Plus whatever turn the set records for a piece modelled side-on.
    child.rotation.y = MODEL_FACING + (turnDeg * Math.PI) / 180;
  }
}

/**
 * Applies the set's scale and stands the model on its own feet.
 *
 * Wrapped in a group so the returned object's origin IS the feet, whatever the
 * exporter did with the model's own.
 */
function stand(scene: Object3D, box: Box3, up: number, across: number): Object3D {
  const centre = new Vector3();
  box.getCenter(centre);

  // Height and plan scale separately, so the offsets that centre the model and
  // stand it on the ground have to use the axis they belong to.
  scene.scale.set(across, up, across);
  scene.position.set(-centre.x * across, -box.min.y * up, -centre.z * across);
  markShadows(scene);

  const root = new Group();
  root.add(scene);
  return root;
}

function markShadows(object: Object3D): void {
  object.traverse((node) => {
    if (node instanceof Mesh) {
      node.castShadow = true;
      node.receiveShadow = true;
    }
  });
}
